import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  useGetNotificationSettingsQuery,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useReadAllNotificationsMutation,
  useSetShowOnlyUnreadFilterMutation,
  type GetNotificationsParams,
  type SetShowOnlyUnreadFilterParams,
} from '../api/notificationsApi';
import type { INotification } from '../models/Notification';
import type { INotificationSettings } from '../models/NotificationSettings';
import {
  emitNotificationsUpdatedEvent,
  getGeneratedNotificationGridKey,
  getGeneratedNotificationGridUrl,
} from '../notificationGeneratedGrid';

interface UseNotificationStateProps {
  openButtonLocalization: string;
  closeButtonLocalization: string;
}

export const useNotificationState = (props: UseNotificationStateProps) => {
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [unreadMessages, setUnreadMessages] = useState<INotification[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<
    INotificationSettings | undefined
  >();
  const [isOpened, setIsOpened] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [listEnded, setListEnded] = useState(false);
  const [notificationParams, setNotificationParams] = useState<GetNotificationsParams | null>(null);

  // Mutable ref: pagination cursor — avoids making currentLength a dep of fetchNotifications
  const currentLengthRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const filterRequestSeqRef = useRef(0);
  const readRequestSeqRef = useRef<Map<number, number>>(new Map());
  const [awaitingFilterListSync, setAwaitingFilterListSync] = useState(false);
  // Skip the filter API call on initial settings load (not a user action)
  const isFilterMount = useRef(true);

  const lastNotificationsMergeWasReplaceRef = useRef(false);
  const prevNotificationIdsRef = useRef<Set<number>>(new Set());
  const prevShowOnlyUnreadRef = useRef(showOnlyUnread);
  const skipResetDueToFilterToggleRef = useRef(false);
  const filterSyncUnreadRef = useRef(showOnlyUnread);

  const [listScrollResetToken, setListScrollResetToken] = useState(0);
  const notificationsRef = useRef<INotification[]>([]);

  const { data: settingsData } = useGetNotificationSettingsQuery();
  const {
    currentData: notificationsData,
    isLoading: isNotificationsInitialLoad,
    isFetching,
  } = useGetNotificationsQuery(notificationParams!, { skip: !notificationParams });
  const loading = isNotificationsInitialLoad || isFetching;
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [readAll] = useReadAllNotificationsMutation();
  const [setOnlyUnread, { isLoading: isFilterMutationLoading }] =
    useSetShowOnlyUnreadFilterMutation();
  const isFilterChanging = awaitingFilterListSync || isFilterMutationLoading;

  const notificationGridUrl = useMemo(
    () => getGeneratedNotificationGridUrl(settingsData),
    [settingsData]
  );

  const notificationGridContentKey = useMemo(
    () => getGeneratedNotificationGridKey(settingsData),
    [settingsData]
  );

  /*
   * These effects bridge RTK Query / list lifecycle into React state (merge, filters, scroll token).
   * Synchronous updates here are intentional; deferring would need snapshot guards against stale merges.
   */
  /* eslint-disable react-hooks/set-state-in-effect -- RTK → local state sync */
  // Map settings + sync filter toggle from server response.
  // Both updates are batched so fetchNotifications picks up the correct showOnlyUnread on initial load.
  useEffect(() => {
    if (settingsData) {
      setNotificationSettings({
        DisplayPeriod: settingsData.DisplayPeriod ?? 15,
        ListSize: settingsData.ListSize ?? 20,
        PushPeriod: settingsData.PushPeriod ?? 60000,
        ShowOnlyUnread: settingsData.ShowOnlyUnread ?? false,
      });
      setShowOnlyUnread(settingsData.ShowOnlyUnread ?? false);
    }
  }, [settingsData]);

  // No longer depends on currentLength (uses ref) — eliminates circular re-creation
  const fetchNotifications = useCallback(
    (emptyList: boolean) => {
      if (!notificationSettings) return;

      if (emptyList) {
        currentLengthRef.current = 0;
        setListEnded(false);
      }
      setNotificationParams({
        showOnlyUnread,
        displayPeriod: notificationSettings.DisplayPeriod,
        listSize: notificationSettings.ListSize,
        currentLength: emptyList ? 0 : currentLengthRef.current,
      });
    },
    [notificationSettings, showOnlyUnread]
  );

  // Merge fetched data into local list
  useEffect(() => {
    if (!notificationsData) return;
    if (notificationsData.Notifications.length === 0) setListEnded(true);

    const isReplace = !notificationParams || notificationParams.currentLength === 0;
    lastNotificationsMergeWasReplaceRef.current = isReplace;

    const onlyUnread = notificationParams?.showOnlyUnread === true;
    const normalizeForFilter = (list: typeof notificationsData.Notifications) =>
      onlyUnread ? list.filter((n) => !n.IsRead) : list;

    if (isReplace) {
      const next = normalizeForFilter(notificationsData.Notifications);
      setNotifications(next);
      currentLengthRef.current = next.length;
    } else {
      // Functional update avoids stale closure on notifications
      setNotifications((prev) => {
        const chunk = normalizeForFilter(notificationsData.Notifications);
        const merged = [...prev, ...chunk];
        const unique = Array.from(new Map(merged.map((n) => [n.Id, n])).values());
        const next = onlyUnread ? unique.filter((n) => !n.IsRead) : unique;
        currentLengthRef.current = next.length;
        return next;
      });
    }

    setUnreadMessages(notificationsData.UnreadedMessages);
    setUnreadCount(notificationsData.UnreadedNotifications);
  }, [notificationsData, notificationParams]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Detect new notification IDs after a list refresh (not pagination)
  useEffect(() => {
    if (notifications.length === 0) {
      prevNotificationIdsRef.current = new Set();
      prevShowOnlyUnreadRef.current = showOnlyUnread;
      skipResetDueToFilterToggleRef.current = false;
      return;
    }

    if (prevShowOnlyUnreadRef.current !== showOnlyUnread) {
      prevShowOnlyUnreadRef.current = showOnlyUnread;
      prevNotificationIdsRef.current = new Set(notifications.map((n) => n.Id));
      skipResetDueToFilterToggleRef.current = true;
      return;
    }

    const prevIds = prevNotificationIdsRef.current;
    const currentIds = new Set(notifications.map((n) => n.Id));

    let hasNewId = false;
    for (const id of currentIds) {
      if (!prevIds.has(id)) {
        hasNewId = true;
        break;
      }
    }

    prevNotificationIdsRef.current = currentIds;

    if (skipResetDueToFilterToggleRef.current) {
      skipResetDueToFilterToggleRef.current = false;
      return;
    }

    if (hasNewId && prevIds.size > 0 && lastNotificationsMergeWasReplaceRef.current) {
      setListScrollResetToken((t) => t + 1);
    }
  }, [notifications, showOnlyUnread]);

  // Initial fetch when settings become available
  useEffect(() => {
    if (notificationSettings) fetchNotifications(true);
  }, [notificationSettings, fetchNotifications]);

  // Polling interval — restarts when settings or filter changes
  useEffect(() => {
    if (!notificationSettings) return;
    intervalRef.current = setInterval(() => {
      currentLengthRef.current = 0;
      fetchNotifications(true);
    }, notificationSettings.PushPeriod);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [notificationSettings, fetchNotifications]);

  // Apply user-toggled filter; skip on initial settings load
  useEffect(() => {
    if (!notificationSettings) return;
    if (isFilterMount.current) {
      isFilterMount.current = false;
      return;
    }
    if (filterSyncUnreadRef.current !== showOnlyUnread) {
      filterSyncUnreadRef.current = showOnlyUnread;
      setNotifications([]);
      setListEnded(false);
    }
    const filterRequestSeq = ++filterRequestSeqRef.current;
    setAwaitingFilterListSync(true);

    const filter: SetShowOnlyUnreadFilterParams = { isShowOnlyUnread: showOnlyUnread };
    setOnlyUnread(filter)
      .unwrap()
      .then(() => {
        if (filterRequestSeq !== filterRequestSeqRef.current) return;
        setListEnded(false);
        currentLengthRef.current = 0;
        fetchNotifications(true);
      })
      .catch((err) => {
        if (filterRequestSeq !== filterRequestSeqRef.current) return;
        console.error('Failed to set filter:', err);
        setAwaitingFilterListSync(false);
      });
  }, [showOnlyUnread, setOnlyUnread, notificationSettings, fetchNotifications]);

  useEffect(() => {
    if (!awaitingFilterListSync) return;
    if (isFilterMutationLoading || isFetching) return;
    if (!notificationParams || notificationParams.showOnlyUnread !== showOnlyUnread) return;
    setAwaitingFilterListSync(false);
  }, [
    awaitingFilterListSync,
    isFilterMutationLoading,
    isFetching,
    notificationParams,
    showOnlyUnread,
  ]);

  // Re-fetch when popover opens and list is empty
  useEffect(() => {
    if (isOpened && notifications.length === 0 && notificationSettings && !loading) {
      fetchNotifications(true);
    }
  }, [isOpened, fetchNotifications, notifications.length, notificationSettings, loading]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const setIsReadNotification = useCallback(
    async (notification: INotification, isRead: boolean) => {
      const notificationId = notification.Id;
      const requestSeq = (readRequestSeqRef.current.get(notificationId) ?? 0) + 1;
      readRequestSeqRef.current.set(notificationId, requestSeq);

      const current = notificationsRef.current.find((n) => n.Id === notificationId);
      const previousIsRead = current ? !!current.IsRead : undefined;

      if (previousIsRead !== undefined && previousIsRead !== isRead) {
        setNotifications((prev) =>
          prev.map((n) => (n.Id !== notificationId ? n : { ...n, IsRead: isRead }))
        );
      }

      if (previousIsRead !== undefined && previousIsRead !== isRead) {
        setUnreadCount((count) => Math.max(0, count + (isRead ? -1 : 1)));
      }

      try {
        await markAsRead({ notification, isRead }).unwrap();
        if (readRequestSeqRef.current.get(notificationId) !== requestSeq) {
          return;
        }
        emitNotificationsUpdatedEvent();
      } catch (err) {
        if (readRequestSeqRef.current.get(notificationId) !== requestSeq) {
          return;
        }
        console.error('Failed to mark notification as read:', err);
        const revertIsRead = previousIsRead;
        if (revertIsRead !== undefined) {
          setNotifications((prev) =>
            prev.map((n) => (n.Id !== notificationId ? n : { ...n, IsRead: revertIsRead }))
          );
          if (revertIsRead !== isRead) {
            setUnreadCount((count) => Math.max(0, count + (isRead ? 1 : -1)));
          }
        }
      }
    },
    [markAsRead]
  );

  const handleReadAllNotifications = useCallback(async () => {
    try {
      await readAll().unwrap();
      setNotifications((prev) => prev.map((n) => ({ ...n, IsRead: true })));
      setUnreadCount(0);
      emitNotificationsUpdatedEvent();
    } catch (err) {
      console.error('Failed to read all notifications:', err);
    }
  }, [readAll]);

  return {
    notificationGridUrl,
    notificationGridContentKey,
    listEnded,
    listScrollResetToken,
    notifications,
    unreadMessages,
    unreadCount,
    isOpened,
    showOnlyUnread,
    isFilterChanging,
    isNotificationsInitialLoad,
    loading,
    notificationSettings,
    setIsOpened,
    fetchNotifications,
    setIsReadNotification,
    handleReadAllNotifications,
    setShowOnlyUnread,
    setUnreadMessages,
    openButtonLocalization: props.openButtonLocalization,
    closeButtonLocalization: props.closeButtonLocalization,
  };
};
